import React from "react";
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Divider, Chip, Alert } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLeaf, faTree, faCarCrash, faExclamationTriangle, faChild, faMapMarkerAlt, faHandPaper } from "@fortawesome/free-solid-svg-icons";
import SectionTitle from "../../components/layouts/others/title-section.layout";

const POST20231219: React.FC = () => {
    return (
        <Container sx={{ mb: 5 }}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS" />

            <Box mt={3} mb={4}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    align="center"
                    gutterBottom
                    color="primary"
                >
                    Sauvons le Bois de la source ! Quelles alternatives ?
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 3, background: "#f8fafb" }}>
                <Alert
                    icon={<FontAwesomeIcon icon={faExclamationTriangle} size="lg" />}
                    severity="warning"
                    sx={{ mb: 2, fontWeight: 500, fontSize: "1.1rem", background: "#ffe8e6" }}
                >
                    Par son entêtement, le Maire a réussi l’exploit de faire l'unanimité contre sa décision de détruire le Bois de la source (rue des Sesçois) pour construire une crèche municipale.
                </Alert>

                <Typography variant="body1" paragraph>
                    <strong>Pourquoi devons-nous conserver intact le site du Bois de la source&nbsp;?</strong>
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faLeaf} color="#43a047" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <>Pour préserver un <b>réservoir de biodiversité</b> riche, la continuité des trames bleue et verte, et des <b>chênes bicentenaires</b> qui stabilisent le sol et forment des îlots de fraîcheur.</>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faTree} color="#388e3c" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Pour arrêter d'artificialiser cette zone après la création des 41 places de parking."
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faExclamationTriangle} color="#fbc02d" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Se prémunir des risques de glissement de terrain dus à l’instabilité hydrogéologique entre la gare et la Seine."
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faCarCrash} color="#1976d2" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Minimiser l'engorgement automobile de la rue des Sesçois déjà saturée aux heures de pointe."
                        />
                    </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Box display="flex" justifyContent="center" gap={2}>
                    <Chip
                        icon={<FontAwesomeIcon icon={faChild} size="sm" />}
                        label="Oui à la construction d'une crèche municipale"
                        color="success"
                        variant="outlined"
                        sx={{ fontWeight: 'bold', fontSize: "1rem" }}
                    />
                    <Chip
                        icon={<FontAwesomeIcon icon={faTree} size="sm" />}
                        label="Non à la destruction du Bois de la source"
                        color="error"
                        variant="filled"
                        sx={{ fontWeight: 'bold', fontSize: "1rem" }}
                    />
                </Box>
            </Paper>

            <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom color="primary">
                    Quels sites alternatifs retenir ?
                </Typography>
                <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Premier site : « Coquement » (Angle ave. Foch et rue Coquement)
                    </Typography>
                    <Typography variant="body2" mb={1}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} color="#1976d2" /> Idéalement situé entre les écoles primaires.<br />
                        <strong>Coût :</strong> Construction + rénovation.
                    </Typography>
                </Box>
                <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Second site : L’esplanade face au restaurant scolaire O. Métra
                    </Typography>
                    <Typography variant="body2">
                        <FontAwesomeIcon icon={faMapMarkerAlt} color="#1976d2" /> Bien situé. <i>N'aurions-nous pas intérêt à se réserver la possibilité d’y construire de nouvelles salles de classe&nbsp;? (question à débattre)</i>
                    </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" paragraph>
                    Contrairement au site du Bois de la source, ces alternatives présentent l’avantage :
                </Typography>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faHandPaper} color="#009688" />
                        </ListItemIcon>
                        <ListItemText
                            primary="de ne pas artificialiser davantage de surfaces"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faTree} color="#388e3c" />
                        </ListItemIcon>
                        <ListItemText
                            primary="de ne pas couper des chênes bicentenaires"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faLeaf} color="#66bb6a" />
                        </ListItemIcon>
                        <ListItemText
                            primary="de ne pas supprimer des îlots de fraîcheur"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faChild} color="#1976d2" />
                        </ListItemIcon>
                        <ListItemText
                            primary="d’être mieux situés d’un point de vue pratique pour les parents"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <FontAwesomeIcon icon={faExclamationTriangle} color="#fbc02d" />
                        </ListItemIcon>
                        <ListItemText
                            primary="d'avoir un coût de construction inférieur (hypothèse à confirmer, surcoût pour Bois de la source à cause du sol argileux gorgé d’eau)."
                        />
                    </ListItem>
                </List>
            </Paper>

            <Paper elevation={0} sx={{ p: 2, background: "#f8fafb" }}>
                <Typography variant="body1" paragraph>
                    Force est de constater que des collectifs, associations et les deux listes politiques d'opposition,
                    tous les acteurs impliqués s'accordent pour enjoindre, Monsieur le Maire, à renoncer à la destruction
                    du Bois de la source et à examiner les deux alternatives proposées.
                </Typography>
                <Alert
                    icon={<FontAwesomeIcon icon={faHandPaper} />}
                    severity="info"
                    sx={{ fontWeight: 500, fontSize: "1.1rem", background: "#e3f2fd", mb: 1 }}
                >
                    Dans l’intérêt général, écoutez les Bacots et leurs représentants dès maintenant.<br />
                    <b>Ne commettez pas l'irréparable&nbsp;!</b>
                </Alert>
            </Paper>

            <Divider sx={{ my: 3 }} />

            <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    La réponse du Maire (courrier du 28/02/2024)&nbsp;:
                </Typography>
                <Box sx={{ background: "#fff3e0", borderLeft: "4px solid #ff9800", p: 2, my: 1 }}>
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                        « … au sein de la majorité municipale, je vous informe que nous avons décidé de confirmer le projet.
                        Je vous invite le jeudi 14 mars à 19h en mairie pour une réunion d'information en présence des architectes
                        et de l'écologue du groupement de maîtrise d'œuvre... »
                    </Typography>
                </Box>
            </Box>
        </Container>
    );
};

export default POST20231219;